import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Input,
  Button,
  Select,
  DatePicker,
  Statistic,
  Tooltip,
  message as antMessage
} from 'antd';
import {
  BarChartOutlined,
  UserOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  DesktopOutlined,
  MobileOutlined,
  TabletOutlined,
  LoginOutlined,
  LogoutOutlined,
  AimOutlined,
  ShoppingCartOutlined,
  TeamOutlined
} from '@ant-design/icons';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';
import './ControleAcessos.css';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ControleAcessos = () => {
  const [acessos, setAcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroUsuario, setFiltroUsuario] = useState('todos'); // 'todos' ou 'franqueados'
  const [dateRange, setDateRange] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [stats, setStats] = useState({
    totalAcessos: 0,
    usuariosUnicos: 0,
    acessosHoje: 0,
    tempoMedioSessao: 0
  });

  useEffect(() => {
    fetchEstatisticas();
    fetchAcessos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalcular estatísticas quando mudar o filtro de usuário
  useEffect(() => {
    if (acessos.length > 0) {
      if (filtroUsuario === 'todos') {
        // Se voltou para "Todos", buscar estatísticas do backend
        fetchEstatisticas();
      } else {
        // Se é "Franqueados", calcular localmente
        calcularEstatisticas(acessos);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroUsuario]);

  // Buscar estatísticas (contadores otimizados do backend)
  const fetchEstatisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Buscar estatísticas completas do backend (sempre para "Todos")
      const response = await fetch(buildApiUrl('/api/acessos/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setStats({
          totalAcessos: data.totalAcessos || 0,
          usuariosUnicos: data.usuariosUnicos || 0,
          acessosHoje: data.acessosHoje || 0,
          tempoMedioSessao: data.tempoMedioSessao || 0
        });
      } else {
        // Fallback será acionado quando fetchAcessos for chamado
      }
    } catch (error) {
      // Fallback será acionado quando fetchAcessos for chamado
    }
  };

  const fetchAcessos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Temporariamente não enviar filtro de franqueados até backend implementar
      // Faremos o filtro localmente
      const response = await fetch(buildApiUrl('/api/acessos?limit=1000'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Mapear event_timestamp para timestamp (compatibilidade com backend)
        const acessosFormatados = (data.acessos || []).map(acesso => ({
          ...acesso,
          timestamp: acesso.timestamp || acesso.event_timestamp || acesso.created_at
        }));
        
        setAcessos(acessosFormatados);
        
        // Se o endpoint de stats não existir ainda, calcular localmente
        if (stats.totalAcessos === 0) {
          calcularEstatisticas(acessosFormatados);
        }
      } else {
        const errorData = await response.json();
        antMessage.error(`Erro ao carregar acessos: ${errorData.message}`);
        setAcessos([]);
      }
    } catch (error) {
      antMessage.error('Erro ao conectar com o servidor. Tente novamente.');
      setAcessos([]);
    } finally {
      setLoading(false);
    }
  };

  const calcularEstatisticas = (dados) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    // Filtrar acessos excluindo logouts (logout não conta como acesso)
    let acessosSemLogout = dados.filter(a => a.tipo !== 'logout');
    
    // Se filtro de franqueados estiver ativo, aplicar também aqui
    if (filtroUsuario === 'franqueados') {
      acessosSemLogout = acessosSemLogout.filter(a => {
        const email = a.usuario || '';
        return email.startsWith('fra.') || email.startsWith('fra_');
      });
    }
    
    const usuariosUnicos = new Set(acessosSemLogout.map(a => a.usuario)).size;
    const acessosHoje = acessosSemLogout.filter(a => {
      if (!a.timestamp) return false;
      const dataAcesso = new Date(a.timestamp);
      return !isNaN(dataAcesso.getTime()) && dataAcesso >= hoje;
    }).length;
    const duracaoMedia = acessosSemLogout.length > 0 
      ? Math.floor(acessosSemLogout.reduce((acc, a) => acc + (a.duracao || 0), 0) / acessosSemLogout.length)
      : 0;

    setStats({
      totalAcessos: acessosSemLogout.length,
      usuariosUnicos,
      acessosHoje,
      tempoMedioSessao: duracaoMedia
    });
  };

  const getTipoTag = (tipo) => {
    const tipoConfig = {
      login: { color: 'green', icon: <LoginOutlined />, text: 'Login' },
      logout: { color: 'red', icon: <LogoutOutlined />, text: 'Logout' },
      click: { color: 'blue', icon: <AimOutlined />, text: 'Click' },
      pageview: { color: 'purple', icon: <EyeOutlined />, text: 'Visualização' },
      adicionar_carrinho: { color: 'cyan', icon: <ShoppingCartOutlined />, text: 'Adicionar ao Carrinho' },
      finalizar_compra: { color: 'gold', icon: <ShoppingCartOutlined />, text: 'Finalizar Compra' }
    };

    const config = tipoConfig[tipo] || { color: 'default', icon: null, text: tipo };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getDispositivoIcon = (dispositivo) => {
    const icons = {
      desktop: <DesktopOutlined style={{ color: '#1890ff' }} />,
      mobile: <MobileOutlined style={{ color: '#52c41a' }} />,
      tablet: <TabletOutlined style={{ color: '#faad14' }} />
    };
    return icons[dispositivo] || <DesktopOutlined />;
  };

  const formatarDuracao = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos}m ${segs}s`;
  };

  const columns = [
    {
      title: 'Usuário',
      dataIndex: 'usuario',
      key: 'usuario',
      width: 280,
      sorter: (a, b) => a.usuario.localeCompare(b.usuario),
      render: (text) => (
        <Space size={8}>
          <UserOutlined style={{ color: '#8c8c8c' }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 140,
      align: 'center',
      filters: [
        { text: 'Login', value: 'login' },
        { text: 'Logout', value: 'logout' },
        { text: 'Click', value: 'click' },
        { text: 'Visualização', value: 'pageview' },
        { text: 'Adicionar ao Carrinho', value: 'adicionar_carrinho' },
        { text: 'Finalizar Compra', value: 'finalizar_compra' },
      ],
      onFilter: (value, record) => record.tipo === value,
      render: (tipo) => getTipoTag(tipo),
    },
    {
      title: 'Página',
      dataIndex: 'pagina',
      key: 'pagina',
      width: 180,
      render: (pagina) => (
        <Text code style={{ fontSize: '12px' }}>{pagina}</Text>
      ),
    },
    {
      title: 'Dispositivo',
      dataIndex: 'dispositivo',
      key: 'dispositivo',
      width: 120,
      align: 'center',
      filters: [
        { text: 'Desktop', value: 'desktop' },
        { text: 'Mobile', value: 'mobile' },
        { text: 'Tablet', value: 'tablet' },
      ],
      onFilter: (value, record) => record.dispositivo === value,
      render: (dispositivo) => (
        <Tooltip title={dispositivo.charAt(0).toUpperCase() + dispositivo.slice(1)}>
          {getDispositivoIcon(dispositivo)}
        </Tooltip>
      ),
    },
    {
      title: 'Navegador',
      dataIndex: 'navegador',
      key: 'navegador',
      width: 120,
      align: 'center',
      render: (navegador) => (
        <Tag>{navegador}</Tag>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 140,
      render: (ip) => (
        <Space size={4}>
          <GlobalOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />
          <Text type="secondary" style={{ fontSize: '12px' }}>{ip}</Text>
        </Space>
      ),
    },
    {
      title: 'Detalhes',
      dataIndex: 'detalhes',
      key: 'detalhes',
      width: 250,
      render: (detalhes, record) => {
        if (!detalhes) return '-';
        
        // Se for um clique no painel, destacar o botão clicado
        if (record.tipo === 'click' && record.pagina === '/painel') {
          return (
            <Tooltip title={detalhes}>
              <Tag color="blue" icon={<AimOutlined />}>
                {detalhes.replace('Clicou no botão: ', '')}
              </Tag>
            </Tooltip>
          );
        }
        
        // Para outros tipos de click ou eventos com detalhes
        if (record.tipo === 'click') {
          return (
            <Tooltip title={detalhes}>
              <Text ellipsis style={{ maxWidth: '220px', display: 'block' }}>
                {detalhes.replace('Clicou no botão: ', '')}
              </Text>
            </Tooltip>
          );
        }
        
        return (
          <Tooltip title={detalhes}>
            <Text type="secondary" ellipsis style={{ maxWidth: '220px', display: 'block' }}>
              {detalhes}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Duração',
      dataIndex: 'duracao',
      key: 'duracao',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.duracao || 0) - (b.duracao || 0),
      render: (duracao) => duracao ? (
        <Text type="secondary">{formatarDuracao(duracao)}</Text>
      ) : '-',
    },
    {
      title: 'Data/Hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      sorter: (a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateA - dateB;
      },
      render: (timestamp) => {
        if (!timestamp) {
          return <Text type="secondary">-</Text>;
        }
        
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          return <Text type="secondary">Data inválida</Text>;
        }
        
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <CalendarOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {date.toLocaleDateString('pt-BR')}
              </Text>
            </Space>
            <Space size={4}>
              <ClockCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {date.toLocaleTimeString('pt-BR')}
              </Text>
            </Space>
          </Space>
        );
      },
    },
  ];

  // Filtrar acessos
  const filteredAcessos = acessos.filter(acesso => {
    // Filtro de tipo de usuário (franqueados)
    if (filtroUsuario === 'franqueados') {
      const email = acesso.usuario || '';
      if (!email.startsWith('fra.') && !email.startsWith('fra_')) {
        return false;
      }
    }

    // Filtro de texto
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchText = 
        acesso.usuario.toLowerCase().includes(searchLower) ||
        acesso.pagina.toLowerCase().includes(searchLower) ||
        acesso.ip.includes(searchLower) ||
        (acesso.detalhes && acesso.detalhes.toLowerCase().includes(searchLower));
      if (!matchText) return false;
    }

    // Filtro de tipo
    if (filtroTipo !== 'todos' && acesso.tipo !== filtroTipo) {
      return false;
    }

    // Filtro de data
    if (dateRange && dateRange.length === 2) {
      if (!acesso.timestamp) return false;
      const acessoData = new Date(acesso.timestamp);
      if (isNaN(acessoData.getTime())) return false;
      const [inicio, fim] = dateRange;
      if (acessoData < inicio || acessoData > fim) {
        return false;
      }
    }

    return true;
  });

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                <BarChartOutlined style={{ marginRight: '16px' }} />
                Controle de Acessos
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Analytics e monitoramento de todas as atividades do portal (Painel, Loja, Chamados, Admin, etc.)
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Cards de Estatísticas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="Total de Acessos"
              value={stats.totalAcessos}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#3e79f7' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="Usuários Únicos"
              value={stats.usuariosUnicos}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="Acessos Hoje"
              value={stats.acessosHoje}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="stat-card">
            <Statistic
              title="Tempo Médio de Sessão"
              value={formatarDuracao(stats.tempoMedioSessao)}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card bordered={false}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={5}>
            <div style={{ position: 'relative' }}>
              <SearchOutlined 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#8c8c8c',
                  fontSize: '16px',
                  zIndex: 1
                }} 
              />
              <Input
                placeholder="Buscar por usuário, página, detalhes ou IP..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ paddingLeft: '40px' }}
                size="large"
              />
            </div>
          </Col>
          <Col xs={24} md={4}>
            <Select
              value={filtroUsuario}
              onChange={setFiltroUsuario}
              style={{ width: '100%' }}
              size="large"
              placeholder="Tipo de Usuário"
            >
              <Option value="todos">Todos</Option>
              <Option value="franqueados">Franqueados</Option>
            </Select>
          </Col>
          <Col xs={24} md={3}>
            <Select
              value={filtroTipo}
              onChange={setFiltroTipo}
              style={{ width: '100%' }}
              size="large"
              placeholder="Tipo de Evento"
            >
              <Option value="todos">Todos os Tipos</Option>
              <Option value="login">Login</Option>
              <Option value="logout">Logout</Option>
              <Option value="click">Click</Option>
              <Option value="pageview">Visualização</Option>
              <Option value="adicionar_carrinho">Adicionar ao Carrinho</Option>
              <Option value="finalizar_compra">Finalizar Compra</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <RangePicker
              onChange={setDateRange}
              style={{ width: '100%' }}
              size="large"
              format="DD/MM/YYYY"
              placeholder={['Data Inicial', 'Data Final']}
            />
          </Col>
          <Col xs={24} md={3}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchEstatisticas();
                fetchAcessos();
              }}
              loading={loading}
              size="large"
              block
            >
              Atualizar
            </Button>
          </Col>
          <Col xs={24} md={3}>
            <Space size={8} style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Text type="secondary">
                Total: <Text strong>{filteredAcessos.length}</Text>
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabela de Acessos */}
      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredAcessos}
          rowKey="id"
          loading={{
            spinning: loading,
            indicator: <Spin size="large" />,
            tip: 'Carregando acessos...',
          }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} acessos`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          locale={{
            emptyText: (
              <Space direction="vertical" size={16} style={{ padding: '60px 0' }}>
                <BarChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  Nenhum acesso encontrado
                </Text>
              </Space>
            ),
            filterConfirm: 'Filtrar',
            filterReset: 'Limpar',
            filterEmptyText: 'Sem filtros',
            selectAll: 'Selecionar página atual',
            selectInvert: 'Inverter seleção',
            selectionAll: 'Selecionar todos',
            sortTitle: 'Ordenar',
            expand: 'Expandir linha',
            collapse: 'Colapsar linha',
            triggerDesc: 'Clique para ordenar decrescente',
            triggerAsc: 'Clique para ordenar crescente',
            cancelSort: 'Clique para cancelar ordenação',
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </Space>
  );
};

export default ControleAcessos;


